import type { MaybeRef } from 'vue'
import useScrollPosition from '@/composables/useScrollPosition'

export type NaviButtonsColor = '#000033' | '#fff'

const isTransparentColor = (color?: string) => {
  const value = color?.trim().toLowerCase() ?? ''
  return (
    value === 'transparent' ||
    value === 'rgba(0, 0, 0, 0)' ||
    value === 'rgba(0,0,0,0)' ||
    value === '#00000000'
  )
}

const isDarkColor = (color?: string) => {
  const value = color?.trim().toLowerCase() ?? ''
  return value === '#000033' || value === 'black'
}

export const useNavbarStyle = (
  navbarColor: MaybeRef<string | undefined>,
  isTransparent?: MaybeRef<boolean | undefined>,
  buttonsColor?: MaybeRef<NaviButtonsColor | '' | undefined>,
) => {
  const color = computed(() => unref(navbarColor) ?? '')
  const { isPageScrolled } = useScrollPosition()

  const isConfiguredTransparent = computed(
    () => Boolean(unref(isTransparent)) || isTransparentColor(color.value),
  )
  const isNavbarTransparent = computed(
    () => isConfiguredTransparent.value && !isPageScrolled.value,
  )
  const solidColor = computed(() =>
    isTransparentColor(color.value) || !color.value ? '#fff' : color.value,
  )
  const overlayTextColor = computed(() => {
    const override = unref(buttonsColor)
    if (isDarkColor(override)) return '#000033'
    return '#fff'
  })
  const isSolidLight = computed(() => isDarkColor(solidColor.value))
  const isNavbarLight = computed(() =>
    isNavbarTransparent.value ? !isDarkColor(unref(buttonsColor)) : isSolidLight.value,
  )

  const navbarStyle = computed(() => ({
    '--navbar-solid-bg': solidColor.value,
    '--navbar-solid-text': isSolidLight.value
      ? 'var(--white)'
      : 'var(--cobalt-800)',
    '--navbar-overlay-text': overlayTextColor.value,
    '--navbar-bg-color': isNavbarTransparent.value
      ? 'transparent'
      : solidColor.value,
    '--navbar-text-color': isNavbarTransparent.value
      ? overlayTextColor.value
      : isSolidLight.value
        ? 'var(--white)'
        : 'var(--cobalt-800)',
    '--navbar-burger-color': isNavbarTransparent.value
      ? overlayTextColor.value
      : isSolidLight.value
        ? 'var(--white)'
        : 'var(--cobalt-600)',
    '--navbar-item-hover-bg': isNavbarLight.value
      ? 'rgba(255, 255, 255, 0.16)'
      : 'var(--paper-100)',
    '--navbar-item-active-bg': isNavbarLight.value
      ? 'rgba(255, 255, 255, 0.16)'
      : 'var(--paper-200)',
  }))

  const burgerButtonStyle = computed(() => ({
    '--navbar-burger-color': 'currentColor',
  }))

  return {
    navbarStyle,
    burgerButtonStyle,
    isNavbarTransparent,
    isNavbarLight,
    isConfiguredTransparent,
  }
}
